import { css } from '@styled-system/css';
import { X } from 'lucide-react';
import { type FC, type ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useModalStack } from '@/shared/contexts/ModalStackContext';
import { useIsMobile } from '@/shared/hooks/useIsMobile';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_WIDTHS: Record<ModalSize, string> = {
  sm: '440px',
  md: '620px',
  lg: '780px',
  xl: '960px',
};

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** Optional subtitle line under the title (text.secondary, bodySm). */
  subtitle?: ReactNode;
  children: ReactNode;
  /** Width preset. `width` prop takes precedence if both are provided. */
  size?: ModalSize;
  width?: string;
  height?: string;
  maxWidth?: string;
  maxHeight?: string;
  footer?: ReactNode;
  hideFooter?: boolean;
  /** Extra content shown in the header next to the title (e.g. a status chip). */
  extra?: ReactNode;
  /** Hide the X close button in the header. Defaults to false. */
  hideCloseButton?: boolean;
  /** Body padding preset. `'none'` removes default padding for full-bleed layouts. Defaults to `'default'`. */
  bodyPadding?: 'default' | 'none';
}

const backdrop = css({
  position: 'fixed',
  inset: 0,
  bg: 'rgba(0, 0, 0, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: 'fadeIn 120ms ease-out',
});

const modalSurface = css({
  bg: 'surface.elevated',
  color: 'text.primary',
  boxShadow: 'lg',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  animation: 'slideInTop 160ms ease-out',
});

const headerStyles = css({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  paddingY: '6',
  paddingX: '8',
  gap: '6',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'border.subtle',
  flexShrink: 0,
});

const titleColumn = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '2',
  flex: 1,
  minWidth: 0,
});

const titleStyles = css({
  fontSize: 'h3',
  fontWeight: 'semibold',
  lineHeight: 'h3',
  color: 'text.primary',
  margin: 0,
});

const subtitleStyles = css({
  fontSize: 'bodySm',
  color: 'text.secondary',
  lineHeight: 'bodySm',
});

const headerActionsStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4',
  flexShrink: 0,
});

const closeBtn = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: 'md',
  bg: 'transparent',
  border: 'none',
  color: 'text.tertiary',
  cursor: 'pointer',
  transition: 'background 120ms ease, color 120ms ease',
  _hover: { bg: 'surface.sunken', color: 'text.primary' },
});

const contentStyles = css({
  flex: 1,
  overflow: 'auto',
  paddingY: '6',
  paddingX: '8',
});

const contentStylesNoPadding = css({
  flex: 1,
  overflow: 'hidden',
  paddingY: '0',
  paddingX: '0',
  minHeight: 0,
});

const footerStyles = css({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '4',
  paddingY: '6',
  paddingX: '8',
  borderTopWidth: '1px',
  borderTopStyle: 'solid',
  borderTopColor: 'border.subtle',
  flexShrink: 0,
});

export const Modal: FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  size,
  width,
  height = 'auto',
  maxWidth = '90vw',
  maxHeight = '90vh',
  footer,
  hideFooter = false,
  extra,
  hideCloseButton = false,
  bodyPadding = 'default',
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const modalId = useId();
  const { registerModal, unregisterModal, getTopModalId } = useModalStack();
  const zIndexRef = useRef<number>(1000);

  useEffect(() => {
    if (isOpen) {
      zIndexRef.current = registerModal(modalId, onClose);
    } else {
      unregisterModal(modalId);
    }
    return () => unregisterModal(modalId);
  }, [isOpen, modalId, onClose, registerModal, unregisterModal]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && getTopModalId() === modalId) onClose();
    };
    if (isOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, modalId, getTopModalId]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && getTopModalId() === modalId) onClose();
  };

  const resolvedWidth = width ?? (size ? SIZE_WIDTHS[size] : 'auto');
  const hasHeader = title || subtitle || extra || !hideCloseButton;
  const hasFooter = !hideFooter && footer != null;

  return createPortal(
    <div className={backdrop} onClick={handleBackdropClick} style={{ zIndex: zIndexRef.current }}>
      <div
        className={modalSurface}
        style={{
          width: isMobile ? '100%' : resolvedWidth,
          height: isMobile ? '100%' : height,
          maxWidth: isMobile ? '100%' : maxWidth,
          maxHeight: isMobile ? '100%' : maxHeight,
          borderRadius: isMobile ? 0 : 'var(--radii-xl)',
        }}
      >
        {hasHeader && (
          <div className={headerStyles}>
            <div className={titleColumn}>
              {title && <h2 className={titleStyles}>{title}</h2>}
              {subtitle && <div className={subtitleStyles}>{subtitle}</div>}
            </div>
            {(extra || !hideCloseButton) && (
              <div className={headerActionsStyles}>
                {extra}
                {!hideCloseButton && (
                  <button
                    type="button"
                    className={closeBtn}
                    onClick={onClose}
                    aria-label={t('common.close')}
                  >
                    <X size={16} strokeWidth={1.75} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        <div className={bodyPadding === 'none' ? contentStylesNoPadding : contentStyles}>{children}</div>
        {hasFooter && <div className={footerStyles}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};
